import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

class AuthService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    this.tokenExpiry = localStorage.getItem('tokenExpiry');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
    this.lastAuthAttempt = null;
    this.authAttemptCount = 0;
    this.isRateLimited = false;
    this.refreshPromise = null; // Prevent multiple simultaneous refresh calls
    this.interceptorsSetup = false; // Prevent duplicate interceptors
    
    // Add token to axios defaults if valid (check manually to avoid calling method before setup complete)
    if (this.token && this.tokenExpiry && Date.now() < parseInt(this.tokenExpiry)) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
    }
    
    // Set up axios interceptor for automatic token refresh
    this.setupAxiosInterceptor();
  }

  isTokenValid() {
    if (!this.token || !this.tokenExpiry) return false;
    return Date.now() < parseInt(this.tokenExpiry);
  }

  async signInWithGoogle() {
    // This method is deprecated - use Google Identity Services directly in the Login component
    // which will call handleGoogleResponse instead
    console.warn('signInWithGoogle is deprecated - use Google Identity Services button instead');
  }

  async handleGoogleResponse(response) {
    // Check if we're rate limited
    if (this.isRateLimited) {
      const timeToWait = this.getWaitTime();
      if (timeToWait <= 0) {
        this.clearRateLimit();
      } else {
        throw new Error(`Rate limited. Please wait ${Math.ceil(timeToWait / 1000)} seconds before trying again.`);
      }
    }

    // Check if too many recent attempts
    const now = Date.now();
    if (this.lastAuthAttempt && (now - this.lastAuthAttempt) < 2000) {
      throw new Error('Please wait before trying again.');
    }

    this.lastAuthAttempt = now;
    this.authAttemptCount++;

    try {
      if (response.credential) {
        // Send the ID token to our backend for verification with timeout
        const apiResponse = await axios.post(`${API_BASE_URL}/api/auth/google`, {
          token: response.credential
        }, {
          timeout: 10000 // 10 second timeout
        });
        
        const { token, user, refreshToken, expiresAt } = apiResponse.data;
        
        this.token = token;
        this.refreshToken = refreshToken;
        this.tokenExpiry = expiresAt;
        this.user = user;
        
        // Reset rate limiting on success
        this.authAttemptCount = 0;
        this.isRateLimited = false;
        
        localStorage.setItem('authToken', token);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('tokenExpiry', expiresAt);
        localStorage.setItem('user', JSON.stringify(user));
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        window.location.reload();
      } else {
        throw new Error('No credential received from Google');
      }
    } catch (error) {
      console.error('Google authentication error:', error);
      
      // Handle rate limiting
      if (error.response?.status === 429 || error.message.includes('429')) {
        this.isRateLimited = true;
        this.rateLimitStartTime = Date.now();
        throw new Error('Too many authentication attempts. Please wait a few minutes before trying again.');
      }
      
      // Exponential backoff for other errors
      if (this.authAttemptCount >= 3) {
        this.isRateLimited = true;
        this.rateLimitStartTime = Date.now();
        throw new Error('Multiple failed attempts. Please wait before trying again.');
      }
      
      throw error;
    }
  }

  getWaitTime() {
    if (!this.rateLimitStartTime) return 0;
    const elapsed = Date.now() - this.rateLimitStartTime;
    const waitTime = Math.min(300000, 30000 * Math.pow(2, this.authAttemptCount - 3)); // Max 5 minutes
    return Math.max(0, waitTime - elapsed);
  }

  clearRateLimit() {
    this.isRateLimited = false;
    this.authAttemptCount = 0;
    this.rateLimitStartTime = null;
  }

  setupAxiosInterceptor() {
    // Prevent duplicate interceptors
    if (this.interceptorsSetup) {
      return;
    }
    this.interceptorsSetup = true;

    // Request interceptor to ensure valid token
    axios.interceptors.request.use(
      async (config) => {
        // Skip token refresh for auth endpoints
        const url = config.url || '';
        const fullUrl = config.baseURL ? config.baseURL + url : url;
        if (url.includes('/api/auth/') || fullUrl.includes('/api/auth/')) {
          return config;
        }

        // Get valid token before making request
        const validToken = await this.getValidToken();
        if (validToken) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${validToken}`;
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for 401 errors
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true;
          
          try {
            const newToken = await this.refreshTokens();
            if (newToken) {
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return axios.request(error.config);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.signOut();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  async getValidToken() {
    // Return existing token if still valid
    if (this.token && this.isTokenValid()) {
      return this.token;
    }

    // Try to refresh if we have a refresh token
    if (this.refreshToken) {
      try {
        return await this.refreshTokens();
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Clear tokens if refresh fails
        this.clearTokens();
        return null;
      }
    }

    return null;
  }

  async refreshTokens() {
    // Prevent multiple simultaneous refresh calls
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = this.doRefresh();
    
    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  async doRefresh() {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
        refreshToken: this.refreshToken
      }, {
        timeout: 10000
      });

      const { token, expiresAt, user } = response.data;
      
      // Update stored tokens
      this.token = token;
      this.tokenExpiry = expiresAt;
      this.user = user;
      
      localStorage.setItem('authToken', token);
      localStorage.setItem('tokenExpiry', expiresAt);
      localStorage.setItem('user', JSON.stringify(user));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return token;
    } catch (error) {
      // If refresh fails, clear all tokens
      this.clearTokens();
      throw error;
    }
  }

  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    this.user = null;
    
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('user');
    
    delete axios.defaults.headers.common['Authorization'];
  }

  async signOut() {
    // Notify backend to invalidate refresh token
    if (this.refreshToken) {
      try {
        await axios.post(`${API_BASE_URL}/api/auth/logout`, {
          refreshToken: this.refreshToken
        }, {
          timeout: 5000
        });
      } catch (error) {
        console.error('Logout notification failed:', error);
        // Continue with local cleanup even if backend call fails
      }
    }
    
    // Clear all tokens and state
    this.clearTokens();
    this.clearRateLimit();
    
    window.location.reload();
  }

  isAuthenticated() {
    return !!(this.token && (this.isTokenValid() || this.refreshToken));
  }

  getUser() {
    return this.user;
  }

}

const authService = new AuthService();
export default authService;